import { useEffect, useMemo, useState } from "react";
import {
  InputBase,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  X,
  Plus,
  Trash2,
  Save,
  ChevronDown,
  FileText,
  Send,
  Stethoscope,
  Copy,
  Pill,
  FlaskConical,
  Eye,
  Phone,
  MessageCircle,
  Images,
  ChevronLeft,
  ChevronRight,
  CreditCard,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const getDraftKey = (patientId) => `consultation_draft_${patientId}`;

const toBase64Url = (value) => {
  const bytes = new TextEncoder().encode(value);
  let binary = "";

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return window
    .btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
};

const buildSafeMediaUrl = (url) => {
  if (!url) return "";

  if (url.startsWith("/")) {
    return BACKEND_URL ? `${BACKEND_URL}${url}` : url;
  }

  const isTwilioUrl =
    /^https?:\/\//i.test(url) &&
    (url.includes("api.twilio.com") || url.includes("twilio.com/2010-04-01"));

  if (isTwilioUrl && BACKEND_URL) {
    return `${BACKEND_URL}/media/${toBase64Url(url)}`;
  }

  return url;
};

const getMediaKind = (media = {}) => {
  const type = String(media.type || "").toLowerCase();
  const url = String(media.url || "").toLowerCase();

  if (type.startsWith("image/") || /\.(jpe?g|png|webp|gif)(\?|$)/i.test(url)) {
    return "image";
  }

  if (type.startsWith("video/") || /\.(mp4|mov|webm|ogg)(\?|$)/i.test(url)) {
    return "video";
  }

  return "";
};

const toDateInputValue = (date) => {
  const safeDate = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(safeDate.getTime())) return "";

  const year = safeDate.getFullYear();
  const month = String(safeDate.getMonth() + 1).padStart(2, "0");
  const day = String(safeDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const todayInputDate = () => toDateInputValue(new Date());

const addDaysInputDate = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return toDateInputValue(date);
};

const createEmptyConsultation = () => ({
  workingDiagnosis: "",
  clientHistory: "",
  investigations: [],
  medications: [""],
  followUpReminderEnabled: true,
  followUpDate: addDaysInputDate(7),
});

const inputClass = "rounded-xl border border-slate-200 bg-white text-slate-800";

const inputSx = {
  px: 2,
  py: 1.2,
  fontSize: "14px",
  color: "#0f172a",
};

const formatNaira = (value) =>
  `₦${Number(value || 0).toLocaleString("en-NG")}`;

const secondaryBtn = {
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "12px",
  px: 2,
  py: 1,
  fontSize: "0.82rem",
  background: "#fff",
  color: "#334155",
  border: "1px solid #e2e8f0",
  boxShadow: "none",
  width: "100%",
  "&:hover": {
    background: "#f8fafc",
    boxShadow: "none",
  },
};

const primaryBtn = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "12px",
  px: 2,
  py: 1,
  fontSize: "0.82rem",
  background: "#075E54",
  color: "#fff",
  boxShadow: "none",
  width: "100%",
  "&:hover": {
    background: "#064b43",
    boxShadow: "none",
  },
};

const Toggle = ({ checked, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative h-6 w-11 rounded-full transition ${
        checked ? "bg-[#075E54]" : "bg-slate-300"
      }`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition ${
          checked ? "left-5" : "left-0.5"
        }`}
      />
    </button>
  );
};

const SectionLabel = ({ children }) => (
  <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
    {children}
  </p>
);

const SidebarAccordion = ({ title, badge, children, defaultExpanded = false }) => (
  <section className="mt-5">
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        border: "1px solid #e2e8f0",
        borderRadius: "16px",
        overflow: "hidden",
        background: "#fff",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ChevronDown size={18} />}
        sx={{
          px: 2,
          py: 1.4,
          "& .MuiAccordionSummary-content": {
            margin: 0,
          },
        }}
      >
        <div className="flex w-full items-center justify-between gap-3">
          <p className="text-sm font-semibold text-slate-950">{title}</p>
          {badge !== undefined && badge !== null && (
            <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-500">
              {badge}
            </span>
          )}
        </div>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  </section>
);

const asArray = (value) => (Array.isArray(value) ? value : []);

const formatDate = (value) => {
  if (!value) return "No date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No date";
  return date.toLocaleDateString("en-NG", {
    month: "short",
    day: "numeric",
    year: "2-digit",
  });
};

const formatDateTime = (value) => {
  if (!value) return "No date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No date";
  return date.toLocaleString("en-NG", {
    day: "numeric",
    month: "short",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const latestRecord = (items = []) => {
  const records = asArray(items);
  if (!records.length) return null;

  return [...records].sort((a, b) => {
    return new Date(b.recordedAt || b.createdAt || 0) - new Date(a.recordedAt || a.createdAt || 0);
  })[0];
};

const toInputDate = (value) => {
  if (!value) return todayInputDate();

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return todayInputDate();

  return toDateInputValue(date);
};

const cleanList = (items = []) =>
  asArray(items)
    .map((item) => String(item || "").trim())
    .filter(Boolean);

const cleanInvestigationRows = (investigations = []) =>
  asArray(investigations).filter(
    (inv) =>
      inv?.name?.trim() ||
      inv?.result?.trim() ||
      inv?.comment?.trim() ||
      inv?.date?.trim()
  );

const formatInvestigationText = (investigations = []) => {
  const rows = cleanInvestigationRows(investigations).map((inv, index) => {
      const parts = [];
      if (inv.name?.trim()) parts.push(inv.name.trim());
      if (inv.result?.trim()) parts.push(`Result: ${inv.result.trim()}`);
      if (inv.comment?.trim()) parts.push(`Comment: ${inv.comment.trim()}`);
      if (inv.date?.trim()) parts.push(`Date: ${inv.date.trim()}`);
      return `${index + 1}. ${parts.join(" | ")}`;
    });

  return rows.join("\n");
};

const createReferralLetterDraft = ({ patient, consultation, doctor }) => {
  const meds = cleanList(consultation?.medications);
  const investigationText = formatInvestigationText(
    consultation?.investigations
  );

  return {
    date: todayInputDate(),
    patientName: patient?.name || "",
    age: patient?.age || "",
    sex: patient?.sex || "",
    phoneNumber: patient?.phoneNumber || "",
    address: patient?.address || "",
    recipientName: "",
    specialty: "",
    facility: "",
    reasonForReferral: consultation?.workingDiagnosis || "",
    urgency: "Routine",
    presentingComplaint: patient?.chiefComplaint || "",
    relevantHistory:
      consultation?.clientHistory || patient?.clientHistory || "",
    provisionalDiagnosis:
      consultation?.workingDiagnosis || patient?.workingDiagnosis || "",
    investigations: investigationText,
    treatmentProvided: meds.length
      ? "Medication and clinical advice were provided during the telemedicine consultation."
      : "Clinical advice was provided during the telemedicine consultation.",
    currentMedications: meds.map((item, index) => `${index + 1}. ${item}`).join("\n"),
    knownAllergies: patient?.allergies || "",
    medicalHistory: patient?.medicalHistory || "",
    doctorName:
      doctor?.name ||
      consultation?.doctorName ||
      patient?.assignedDoctor?.name ||
      "Zaviramed Doctor",
    doctorDate: todayInputDate(),
    consultationId: consultation?._id || "",
  };
};

const createConsultationDocumentDraft = ({
  type,
  patient,
  consultation,
  doctor,
}) => {
  const diagnosis = consultation
    ? consultation?.workingDiagnosis || patient?.workingDiagnosis || ""
    : "";
  const doctorName =
    doctor?.name ||
    consultation?.doctorName ||
    consultation?.doctor?.name ||
    patient?.assignedDoctor?.name ||
    "Zaviramed Doctor";

  return {
    type,
    consultationId: consultation?._id || "",
    consultationDate: consultation?.date || new Date().toISOString(),
    diagnosis,
    doctorName,
    notes:
      type === "prescription"
        ? "Please use exactly as directed by your doctor."
        : "Please complete the listed investigations as discussed with your doctor.",
    drugs: cleanList(consultation?.medications).length
      ? cleanList(consultation?.medications)
      : [""],
    investigations: cleanInvestigationRows(consultation?.investigations).length
      ? cleanInvestigationRows(consultation?.investigations).map((item) => ({
          name: item.name || "",
          result: item.result || "",
          comment: item.comment || "",
          date: item.date || "",
        }))
      : [{ name: "", result: "", comment: "", date: "" }],
  };
};

const MiniTrend = ({ points = [] }) => {
  const values = asArray(points)
    .map((item) => Number(item.value))
    .filter(Number.isFinite);

  if (values.length < 2) {
    return (
      <div className="flex h-10 items-center justify-center rounded-lg bg-slate-50 text-[11px] text-slate-400">
        Add more records
      </div>
    );
  }

  const width = 120;
  const height = 38;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = width / (values.length - 1);
  const polyline = values
    .map((value, index) => {
      const x = index * step;
      const y = height - ((value - min) / range) * (height - 8) - 4;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg className="h-10 w-full text-[#075E54]" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Health trend">
      <polyline points={polyline} fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const HealthMetric = ({ label, value, date, points }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-xs font-medium text-slate-400">{label}</p>
        <p className="mt-1 text-base font-semibold text-slate-950">{value || "No record"}</p>
      </div>
      <span className="shrink-0 rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-500">
        {date || "New"}
      </span>
    </div>
    <div className="mt-3">
      <MiniTrend points={points} />
    </div>
  </div>
);

const PatientHealthSnapshot = ({ patient }) => {
  const tracking = patient?.healthTracking || {};
  const bp = latestRecord(tracking.bloodPressure);
  const sugar = latestRecord(tracking.bloodSugar);
  const bmi = latestRecord(tracking.bmi);
  const weight = latestRecord(tracking.weight);
  const symptom = latestRecord(tracking.symptoms);
  const mood = latestRecord(tracking.mood);
  const cycle = latestRecord(tracking.cycle);

  const metrics = [
    {
      label: "BMI",
      value: bmi?.bmi ? Number(bmi.bmi).toFixed(1) : "",
      date: formatDate(bmi?.recordedAt),
      points: asArray(tracking.bmi).map((item) => ({
        value: Number(item.bmi),
      })),
    },
    {
      label: "Blood pressure",
      value: bp?.systolic && bp?.diastolic ? `${bp.systolic}/${bp.diastolic}` : "",
      date: formatDate(bp?.recordedAt),
      points: asArray(tracking.bloodPressure).map((item) => ({
        value: Number(item.systolic),
      })),
    },
    {
      label: "Blood sugar",
      value: sugar?.value ? `${sugar.value} ${sugar.unit || "mg/dL"}` : "",
      date: formatDate(sugar?.recordedAt),
      points: asArray(tracking.bloodSugar).map((item) => ({
        value: Number(item.value),
      })),
    },
    {
      label: "Weight",
      value: weight?.value ? `${weight.value} ${weight.unit || "kg"}` : "",
      date: formatDate(weight?.recordedAt),
      points: asArray(tracking.weight).map((item) => ({
        value: Number(item.value),
      })),
    },
  ];

  const hasAnyRecord =
    metrics.some((item) => item.value) || symptom || mood || cycle;
  const healthRecordCount =
    metrics.filter((item) => item.value).length +
    [symptom, mood, cycle].filter(Boolean).length;

  return (
    <section className="mt-5">
      <Accordion
        defaultExpanded={false}
        disableGutters
        elevation={0}
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: "16px",
          overflow: "hidden",
          background: "#fff",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ChevronDown size={18} />}
          sx={{
            px: 2,
            py: 1.4,
            "& .MuiAccordionSummary-content": {
              margin: 0,
            },
          }}
        >
          <div className="flex w-full items-center justify-between">
            <p className="text-sm font-semibold text-slate-950">
              Health records
            </p>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-500">
              {hasAnyRecord ? healthRecordCount : 0}
            </span>
          </div>
        </AccordionSummary>

        <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
          <div className="grid gap-3">
            {metrics.map((item) => (
              <HealthMetric key={item.label} {...item} />
            ))}
          </div>

          <div className="mt-4 grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
            <div className="flex justify-between gap-3">
              <span>Symptoms</span>
              <strong className="text-right text-slate-900">
                {symptom?.text || "No record"}
              </strong>
            </div>
            <div className="flex justify-between gap-3">
              <span>Mood</span>
              <strong className="text-right text-slate-900">
                {mood?.mood || "No record"}
              </strong>
            </div>
            <div className="flex justify-between gap-3">
              <span>Cycle</span>
              <strong className="text-right text-slate-900">
                {cycle?.periodStart ? formatDate(cycle.periodStart) : "No record"}
              </strong>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </section>
  );
};

const PatientInfoPanel = ({
  setInfoDrawerOpen,
  patient,
  setReferralOpen,
  setOpenPrescriptions,
  openSpecialistConsultation,
  setOpenSpecialistConsultation,
  isPaid,
  onSendCustomPaymentLink,
  customPaymentSending = false,
}) => {
  const [consultations, setConsultations] = useState([]);
  const [referralLetters, setReferralLetters] = useState([]);
  const [activeConsultation, setActiveConsultation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [draftStatus, setDraftStatus] = useState("");
  const [sendForCosting, setSendForCosting] = useState(false);
  const [pharmacies, setPharmacies] = useState([]);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [searchPharmacy, setSearchPharmacy] = useState("");
  const [sendPatientPrescription, setSendPatientPrescription] = useState(false);
  const [sendPatientInvestigations, setSendPatientInvestigations] =
    useState(false);
  const [latestCostingRequest, setLatestCostingRequest] = useState(null);
  const [referralLetterOpen, setReferralLetterOpen] = useState(false);
  const [referralLetter, setReferralLetter] = useState(null);
  const [sendingReferralLetter, setSendingReferralLetter] = useState(false);
  const [documentModal, setDocumentModal] = useState(null);
  const [sendingDocument, setSendingDocument] = useState(false);
  const [documentSendToPharmacy, setDocumentSendToPharmacy] = useState(false);
  const [documentSelectedPharmacy, setDocumentSelectedPharmacy] =
    useState(null);
  const [documentPharmacySearch, setDocumentPharmacySearch] = useState("");
  const [documentConfirmOpen, setDocumentConfirmOpen] = useState(false);
  const [savedPrescriptions, setSavedPrescriptions] = useState([]);
  const [savedInvestigations, setSavedInvestigations] = useState([]);
  const [complaintOpen, setComplaintOpen] = useState(false);
  const [mediaGalleryOpen, setMediaGalleryOpen] = useState(false);
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const [customPaymentOpen, setCustomPaymentOpen] = useState(false);
  const [customPaymentAmount, setCustomPaymentAmount] = useState("");
  const [customPaymentDescription, setCustomPaymentDescription] = useState("");
  const [customPaymentError, setCustomPaymentError] = useState("");

  useEffect(() => {
    setConsultations(patient?.consultations || []);
    setReferralLetters(patient?.referralLetters || []);
  }, [patient?._id, patient?.consultations, patient?.referralLetters]);

  useEffect(() => {
    setComplaintOpen(false);
    setMediaGalleryOpen(false);
    setActiveMediaIndex(0);
  }, [patient?._id]);

  useEffect(() => {
    fetchLatestCostingRequest();
    fetchDocumentHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?._id]);

  const doctor = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("doctor") || "{}");
    } catch {
      return {};
    }
  }, []);

  const updateField = (key, value) =>
    setActiveConsultation((prev) => ({ ...prev, [key]: value }));

  const updateReferralLetter = (key, value) =>
    setReferralLetter((prev) => ({ ...(prev || {}), [key]: value }));

  const updateInvestigation = (index, key, value) => {
    const copy = [...(activeConsultation?.investigations || [])];
    copy[index] = { ...copy[index], [key]: value };
    updateField("investigations", copy);
  };

  const startNewConsultation = () => {
    setActiveConsultation(createEmptyConsultation());
    setDraftStatus("");
  };

  const clearDraft = () => {
    if (!patient?._id) return;
    localStorage.removeItem(getDraftKey(patient._id));
    setDraftStatus("Draft cleared");
  };

  const cancelActiveConsultation = () => {
    setActiveConsultation(null);
    setDraftStatus("");
    clearDraft();
  };

  const normalizeCostingStatus = (status = "") => {
    if (status === "pending") return "uncosted";
    if (status === "closed") return "delivered";
    return status || "uncosted";
  };

  const fetchLatestCostingRequest = async () => {
    if (!patient?._id) return;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/prescriptions`,
        {
          params: { patientId: patient._id },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const latest = (res.data?.requests || [])[0] || null;
      setLatestCostingRequest(latest);
    } catch (err) {
      console.error("Failed to fetch patient costing request", err);
    }
  };

  const fetchDocumentHistory = async () => {
    if (!patient?._id) return;

    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };

    try {
      const [prescriptionRes, investigationRes] = await Promise.allSettled([
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/prescriptions/client/${patient._id}`,
          {
            params: { limit: 50 },
            headers,
          }
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/investigations/client/${patient._id}`,
          {
            params: { limit: 50 },
            headers,
          }
        ),
      ]);

      if (prescriptionRes.status === "fulfilled") {
        setSavedPrescriptions(prescriptionRes.value.data?.data || []);
      }

      if (investigationRes.status === "fulfilled") {
        setSavedInvestigations(investigationRes.value.data?.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch document history", err);
    }
  };

  const resetDocumentPharmacy = () => {
    setDocumentSendToPharmacy(false);
    setDocumentSelectedPharmacy(null);
    setDocumentPharmacySearch("");
    setDocumentConfirmOpen(false);
  };

  const openPharmacyCall = (pharmacy = {}) => {
    const number = pharmacy.phoneNumber;
    if (!number) return;
    window.location.href = `tel:${number}`;
  };

  const openPharmacyWhatsapp = (pharmacy = {}) => {
    const number = String(
      pharmacy.whatsappNumber || pharmacy.phoneNumber || ""
    ).replace(/[^\d]/g, "");

    if (!number) return;
    window.open(`https://wa.me/${number}`, "_blank", "noopener,noreferrer");
  };

  const openReferralLetter = (consultation = null) => {
    setReferralLetter(
      createReferralLetterDraft({
        patient,
        consultation:
          consultation ||
          activeConsultation ||
          consultations?.[0] ||
          createEmptyConsultation(),
        doctor,
      })
    );
    setReferralLetterOpen(true);
    setDraftStatus("");
  };

  const openSavedReferralLetter = (savedLetter) => {
    const blankDraft = createReferralLetterDraft({
      patient,
      consultation: createEmptyConsultation(),
      doctor,
    });

    setReferralLetter({
      ...blankDraft,
      ...savedLetter,
      date: toInputDate(savedLetter?.date),
      doctorDate: toInputDate(savedLetter?.doctorDate || savedLetter?.date),
      consultationId: savedLetter?.consultationId || "",
    });
    setReferralLetterOpen(true);
    setDraftStatus("");
  };

  const closeReferralLetter = () => {
    if (sendingReferralLetter) return;
    setReferralLetterOpen(false);
  };

  const sendReferralLetter = async () => {
    if (!patient?._id || !referralLetter) return;

    try {
      setSendingReferralLetter(true);

      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/clients/${patient._id}/referral-letter`,
        {
          letter: referralLetter,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || null}`,
          },
        }
      );

      if (res.data?.success) {
        if (res.data.referralLetter) {
          setReferralLetters((prev) => [res.data.referralLetter, ...prev]);
        }

        setReferralLetterOpen(false);
        setDraftStatus("Referral letter sent to patient email");
      } else {
        setDraftStatus(res.data?.message || "Referral letter could not be sent");
      }
    } catch (err) {
      console.error("Failed to send referral letter", err);
      setDraftStatus(
        err.response?.data?.message || "Failed to send referral letter"
      );
    } finally {
      setSendingReferralLetter(false);
    }
  };

  const openConsultationDocument = (consultation, type) => {
    resetDocumentPharmacy();
    setDocumentModal(
      createConsultationDocumentDraft({
        type,
        patient,
        consultation,
        doctor,
      })
    );
    setDraftStatus("");
  };

  const openHistoryDocument = (item) => {
    resetDocumentPharmacy();
    setDocumentModal({
      type: item.type,
      consultationId: item.consultationId || "",
      consultationDate: item.date || new Date().toISOString(),
      diagnosis: item.diagnosis || "",
      doctorName: item.doctorName || doctor?.name || "Zaviramed Doctor",
      notes: item.notes || "",
      drugs: item.type === "prescription" ? item.drugs || [""] : [""],
      investigations:
        item.type === "investigations"
          ? item.investigations || [{ name: "", result: "", comment: "", date: "" }]
          : [{ name: "", result: "", comment: "", date: "" }],
    });
    setDraftStatus("");
  };

  const closeConsultationDocument = () => {
    if (sendingDocument) return;
    setDocumentModal(null);
    resetDocumentPharmacy();
  };

  const updateDocumentDraft = (key, value) => {
    setDocumentModal((prev) => ({ ...(prev || {}), [key]: value }));
  };

  const updateDocumentDrug = (index, value) => {
    setDocumentModal((prev) => {
      const drugs = [...(prev?.drugs || [])];
      drugs[index] = value;
      return { ...(prev || {}), drugs };
    });
  };

  const updateDocumentInvestigation = (index, key, value) => {
    setDocumentModal((prev) => {
      const investigations = [...(prev?.investigations || [])];
      investigations[index] = {
        ...(investigations[index] || {}),
        [key]: value,
      };
      return { ...(prev || {}), investigations };
    });
  };

  const requestSendConsultationDocument = () => {
    if (!documentModal) return;

    const diagnosis = String(documentModal.diagnosis || "").trim();

    if (!diagnosis) {
      setDraftStatus("Add a diagnosis before sending.");
      return;
    }

    if (documentModal.type === "prescription") {
      if (!cleanList(documentModal.drugs).length) {
        setDraftStatus("Add at least one medication before sending.");
        return;
      }

      if (documentSendToPharmacy && !documentSelectedPharmacy?._id) {
        setDraftStatus("Choose a pharmacy before sending.");
        return;
      }
    } else if (
      !cleanInvestigationRows(documentModal.investigations).length
    ) {
      setDraftStatus("Add at least one investigation before sending.");
      return;
    }

    setDocumentConfirmOpen(true);
  };

  const sendConsultationDocument = async () => {
    if (!patient?._id || !documentModal) return;

    try {
      setSendingDocument(true);
      setDocumentConfirmOpen(false);
      const diagnosis = String(documentModal.diagnosis || "").trim();

      if (!diagnosis) {
        setDraftStatus("Add a diagnosis before sending.");
        return;
      }

      if (documentModal.type === "prescription") {
        const drugs = cleanList(documentModal.drugs);

        if (!drugs.length) {
          setDraftStatus("Add at least one medication before sending.");
          return;
        }

        if (documentSendToPharmacy && !documentSelectedPharmacy?._id) {
          setDraftStatus("Choose a pharmacy before sending.");
          return;
        }

        const prescriptionRes = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/prescriptions/send`,
          {
            clientId: patient._id,
            drugs,
            notes: documentModal.notes || "",
            diagnosis,
            doctorName: documentModal.doctorName || doctor?.name,
            doctorId: doctor?._id || doctor?.id,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (prescriptionRes.data?.prescription) {
          setSavedPrescriptions((prev) => [
            prescriptionRes.data.prescription,
            ...prev,
          ]);
        }

        let pharmacyMessage = "";

        if (documentSendToPharmacy && documentSelectedPharmacy?._id) {
          try {
            const costingRes = await axios.post(
              `${process.env.REACT_APP_BACKEND_URL}/prescriptions`,
              {
                patientId: patient._id,
                pharmacyId: documentSelectedPharmacy._id,
                drugs: drugs.map((name) => ({
                  name,
                  quantity: "1",
                })),
              },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              }
            );

            setLatestCostingRequest(costingRes.data?.request || null);
            pharmacyMessage = " Sent to pharmacy for costing.";
          } catch (pharmacyErr) {
            console.error("Failed to send prescription to pharmacy", pharmacyErr);
            pharmacyMessage = " Pharmacy costing request failed.";
          }
        }

        setDraftStatus(
          `${
            documentModal.consultationId
              ? "Prescription resent to patient email."
              : "Prescription sent to patient email."
          }${pharmacyMessage}`
        );
      } else {
        const investigations = cleanInvestigationRows(
          documentModal.investigations
        );

        if (!investigations.length) {
          setDraftStatus("Add at least one investigation before sending.");
          return;
        }

        const investigationRes = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/investigations/send`,
          {
            clientId: patient._id,
            investigations,
            notes: documentModal.notes || "",
            diagnosis,
            doctorName: documentModal.doctorName || doctor?.name,
            doctorId: doctor?._id || doctor?.id,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (investigationRes.data?.investigation) {
          setSavedInvestigations((prev) => [
            investigationRes.data.investigation,
            ...prev,
          ]);
        }

        setDraftStatus(
          documentModal.consultationId
            ? "Investigations resent to patient email."
            : "Investigations sent to patient email."
        );
      }

      setDocumentModal(null);
      resetDocumentPharmacy();
    } catch (err) {
      console.error("Failed to send consultation document", err);
      setDraftStatus(
        err.response?.data?.message || "Failed to send document to patient."
      );
    } finally {
      setSendingDocument(false);
    }
  };

  const completeConsultation = async () => {
    if (!patient?._id || !activeConsultation) return;

    try {
      setSaving(true);

      const payload = {
        ...activeConsultation,
        medications: (activeConsultation.medications || [])
          .map((m) => m.trim())
          .filter(Boolean),
        investigations: cleanInvestigationRows(activeConsultation.investigations),
        doctorName: doctor?.name,
        doctorId: doctor?._id,
        followUpReminderEnabled:
          activeConsultation.followUpReminderEnabled !== false,
        followUpDate:
          activeConsultation.followUpReminderEnabled === false
            ? ""
            : activeConsultation.followUpDate || addDaysInputDate(7),
      };

      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/clients/${patient._id}/consultations`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || null}`,
          },
        }
      );

      const postSaveMessages = [];

      if (sendForCosting && selectedPharmacy) {
        const costingRes = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/prescriptions`,
          {
            patientId: patient._id,
            pharmacyId: selectedPharmacy._id,
            drugs: activeConsultation.medications.map((m) => ({
              name: m,
              quantity: "1",
            })),
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        setLatestCostingRequest(costingRes.data?.request || null);
        postSaveMessages.push("Sent to pharmacy for costing");
      }

      if (sendPatientPrescription) {
        try {
          const prescriptionRes = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/prescriptions/send`,
            {
              clientId: patient._id,
              drugs: payload.medications || null,
              notes: "Check doctor chat for notes.",
              diagnosis: payload.workingDiagnosis || "Not specified",
              doctorName: doctor?.name,
              doctorId: doctor?._id || doctor?.id,
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          if (prescriptionRes.data?.prescription) {
            setSavedPrescriptions((prev) => [
              prescriptionRes.data.prescription,
              ...prev,
            ]);
          }
          postSaveMessages.push("Prescription sent");
        } catch (err) {
          console.error("Failed to send prescription to patient", err);
          postSaveMessages.push("Prescription failed");
        }
      }

      if (sendPatientInvestigations && payload.investigations.length > 0) {
        try {
          const investigationRes = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/investigations/send`,
            {
              clientId: patient._id,
              investigations: payload.investigations,
              notes: "Please complete the listed investigations as discussed with your doctor.",
              diagnosis: payload.workingDiagnosis || "Not specified",
              doctorName: doctor?.name,
              doctorId: doctor?._id || doctor?.id,
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          if (investigationRes.data?.investigation) {
            setSavedInvestigations((prev) => [
              investigationRes.data.investigation,
              ...prev,
            ]);
          }
          postSaveMessages.push("Investigations sent");
        } catch (err) {
          console.error("Failed to send investigations to patient", err);
          postSaveMessages.push("Investigations failed");
        }
      } else if (sendPatientInvestigations) {
        postSaveMessages.push("No investigations to send");
      }

      setConsultations((prev) => [res.data, ...prev]);
      setActiveConsultation(null);
      localStorage.removeItem(getDraftKey(patient._id));
      setDraftStatus(["Consultation saved", ...postSaveMessages].join(". "));
      setSendPatientPrescription(false);
      setSendPatientInvestigations(false);
      setSendForCosting(false);
      setSelectedPharmacy(null);
    } catch (err) {
      console.error("Failed to save consultation", err);
      setDraftStatus("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const fetchPharmacies = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/pharmacies`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setPharmacies(res.data.pharmacies || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (sendForCosting || documentSendToPharmacy) fetchPharmacies();
  }, [sendForCosting, documentSendToPharmacy]);

  const filteredPharmacies = useMemo(() => {
    let list = pharmacies;

    if (searchPharmacy) {
      list = list.filter((p) =>
        p.name.toLowerCase().includes(searchPharmacy.toLowerCase())
      );
    }

    return list;
  }, [pharmacies, searchPharmacy]);

  const filteredDocumentPharmacies = useMemo(() => {
    let list = pharmacies;

    if (documentPharmacySearch) {
      list = list.filter((p) =>
        p.name.toLowerCase().includes(documentPharmacySearch.toLowerCase())
      );
    }

    return list;
  }, [pharmacies, documentPharmacySearch]);

  const prescriptionHistory = useMemo(() => {
    const stored = asArray(savedPrescriptions).map((prescription) => ({
      key: `stored-prescription-${prescription._id}`,
      type: "prescription",
      source: "Saved PDF",
      date: prescription.sentAt || prescription.createdAt,
      diagnosis: prescription.diagnosis || "Prescription",
      doctorName: prescription.doctorName || "Zaviramed Doctor",
      notes: prescription.notes || "",
      drugs: cleanList(prescription.drugs),
    }));

    const fromConsultations = asArray(consultations)
      .filter((consultation) => cleanList(consultation.medications).length)
      .map((consultation) => ({
        key: `consultation-prescription-${consultation._id}`,
        type: "prescription",
        source: "Consultation",
        consultationId: consultation._id,
        date: consultation.date,
        diagnosis: consultation.workingDiagnosis || "No diagnosis",
        doctorName:
          consultation.doctorName ||
          consultation.doctor?.name ||
          "Zaviramed Doctor",
        notes: "Please use exactly as directed by your doctor.",
        drugs: cleanList(consultation.medications),
      }));

    return [...stored, ...fromConsultations]
      .filter((item) => item.drugs.length)
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [savedPrescriptions, consultations]);

  const investigationHistory = useMemo(() => {
    const stored = asArray(savedInvestigations).map((investigation) => ({
      key: `stored-investigation-${investigation._id}`,
      type: "investigations",
      source: "Saved PDF",
      date: investigation.sentAt || investigation.createdAt,
      diagnosis: investigation.diagnosis || "Investigation request",
      doctorName: investigation.doctorName || "Zaviramed Doctor",
      notes: investigation.notes || "",
      investigations: cleanInvestigationRows(investigation.investigations),
    }));

    const fromConsultations = asArray(consultations)
      .filter((consultation) =>
        cleanInvestigationRows(consultation.investigations).length
      )
      .map((consultation) => ({
        key: `consultation-investigation-${consultation._id}`,
        type: "investigations",
        source: "Consultation",
        consultationId: consultation._id,
        date: consultation.date,
        diagnosis: consultation.workingDiagnosis || "No diagnosis",
        doctorName:
          consultation.doctorName ||
          consultation.doctor?.name ||
          "Zaviramed Doctor",
        notes:
          "Please complete the listed investigations as discussed with your doctor.",
        investigations: cleanInvestigationRows(consultation.investigations),
      }));

    return [...stored, ...fromConsultations]
      .filter((item) => item.investigations.length)
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [savedInvestigations, consultations]);

  const mediaGalleryItems = useMemo(() => {
    return asArray(patient?.messages)
      .flatMap((message, messageIndex) =>
        asArray(message.media)
          .map((media, mediaIndex) => {
            const kind = getMediaKind(media);
            const url = buildSafeMediaUrl(media?.url || "");

            if (!kind || !url) return null;

            return {
              id:
                media._id ||
                `${message._id || messageIndex}-${media.url || mediaIndex}`,
              kind,
              url,
              type: media.type || "",
              sender: message.sender || "client",
              caption: message.text || "",
              timestamp: message.timestamp,
            };
          })
          .filter(Boolean)
      )
      .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
  }, [patient?.messages]);

  useEffect(() => {
    if (activeMediaIndex <= mediaGalleryItems.length - 1) return;
    setActiveMediaIndex(0);
  }, [activeMediaIndex, mediaGalleryItems.length]);

  const openMediaGallery = () => {
    if (!mediaGalleryItems.length) {
      setDraftStatus("No patient images or videos yet.");
      return;
    }

    setActiveMediaIndex(0);
    setMediaGalleryOpen(true);
    setDraftStatus("");
  };

  const showPreviousMedia = () => {
    setActiveMediaIndex((current) =>
      mediaGalleryItems.length
        ? (current - 1 + mediaGalleryItems.length) % mediaGalleryItems.length
        : 0
    );
  };

  const showNextMedia = () => {
    setActiveMediaIndex((current) =>
      mediaGalleryItems.length
        ? (current + 1) % mediaGalleryItems.length
        : 0
    );
  };

  const sendCustomPaymentLink = async () => {
    const amount = Number(customPaymentAmount || 0);
    const description = customPaymentDescription.trim();

    if (!amount || amount < 1) {
      setCustomPaymentError("Enter the amount the patient should pay.");
      return;
    }

    if (!description) {
      setCustomPaymentError("Enter what this payment is for.");
      return;
    }

    if (!onSendCustomPaymentLink) {
      setCustomPaymentError("Payment sending is not ready on this screen.");
      return;
    }

    setCustomPaymentError("");

    try {
      const result = await onSendCustomPaymentLink({ amount, description });

      if (result?.warning) {
        setCustomPaymentError(result.message || "Payment link was created.");
        return;
      }

      setCustomPaymentOpen(false);
      setCustomPaymentAmount("");
      setCustomPaymentDescription("");
      setDraftStatus("Payment link sent");
    } catch (err) {
      setCustomPaymentError(err.message || "Payment link could not be sent.");
    }
  };

  if (!patient) return null;

  const initials = patient.name
    ?.split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const generateSummary = (consultation) => {
    if (!consultation || !patient) return "";

    const meds = (consultation.medications || [])
      .map((m) => m.trim())
      .filter(Boolean);

    const investigations = (consultation.investigations || []).filter(
      (inv) =>
        inv?.name?.trim() ||
        inv?.result?.trim() ||
        inv?.comment?.trim() ||
        inv?.date?.trim()
    );

    return `
CONSULTATION SUMMARY

Patient: ${patient.name}
Age/Sex: ${patient.age} yrs / ${patient.sex}
Date: ${new Date().toLocaleDateString()}
Doctor: ${doctor?.name || "Doctor"}

Chief Complaint:
${patient.chiefComplaint || "—"}

Working Diagnosis:
${consultation.workingDiagnosis || "—"}

Clinical Notes:
${consultation.clientHistory || "—"}

Follow-up Reminder:
${
  consultation.followUpReminderEnabled === false
    ? "No reminder set"
    : formatDate(consultation.followUpDate || addDaysInputDate(7))
}

Medications:
${meds.length ? meds.map((m, i) => `${i + 1}. ${m}`).join("\n") : "—"}

Investigations:
${
  investigations.length
    ? investigations
        .map((inv, i) => {
          const parts = [];
          if (inv.name) parts.push(inv.name);
          if (inv.result) parts.push(`Result: ${inv.result}`);
          if (inv.comment) parts.push(`Comment: ${inv.comment}`);
          if (inv.date) parts.push(`Date: ${inv.date}`);
          return `${i + 1}. ${parts.join(" | ")}`;
        })
        .join("\n")
    : "—"
}

Advice:
Follow prescribed medications and complete investigations as discussed.
    `.trim();
  };

  const copySummary = async (consultation) => {
    try {
      const text = generateSummary(consultation);
      await navigator.clipboard.writeText(text);
      setDraftStatus("Summary copied");
    } catch (err) {
      console.error(err);
      setDraftStatus("Copy failed");
    }
  };

  const activeInvestigationCount = cleanInvestigationRows(
    activeConsultation?.investigations
  ).length;
  const documentIsPrescription = documentModal?.type === "prescription";
  const documentTitle = documentIsPrescription
    ? "Prescription"
    : "Investigations";
  const documentItemCount = documentIsPrescription
    ? cleanList(documentModal?.drugs).length
    : cleanInvestigationRows(documentModal?.investigations).length;
  const documentHasDiagnosis = Boolean(
    String(documentModal?.diagnosis || "").trim()
  );
  const documentCanSend = documentItemCount > 0 && documentHasDiagnosis;
  const activeMedia = mediaGalleryItems[activeMediaIndex] || mediaGalleryItems[0];
  const actionBadgeCount = isPaid ? 9 : 7;

  return (
    <aside className="relative flex h-full w-[100vw] flex-col overflow-y-auto bg-white px-4 py-5 lg:h-[100vh] lg:w-[360px] lg:pb-24">
      <button
        type="button"
        onClick={() => setInfoDrawerOpen?.(false)}
        className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 lg:hidden"
      >
        <X size={16} />
      </button>

      {/* Patient */}
      <section className="pr-10 lg:pr-0">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
            {initials || "PT"}
          </div>

          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-slate-950">
              {patient.name}
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              {patient.age || "—"} yrs · {patient.sex || "—"}
            </p>
          </div>
        </div>
      </section>

      {/* Complaint */}
      <section className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <button
          type="button"
          onClick={() => setComplaintOpen((current) => !current)}
          className="flex w-full items-center justify-between gap-3 text-left"
          aria-expanded={complaintOpen}
        >
          <SectionLabel>Chief complaint</SectionLabel>
          <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500">
            {complaintOpen ? "Hide" : "Show"}
            <ChevronDown
              size={15}
              className={`transition ${complaintOpen ? "rotate-180" : ""}`}
            />
          </span>
        </button>

        {complaintOpen ? (
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">
            {patient.chiefComplaint || "—"}
          </p>
        ) : (
          <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-500">
            {patient.chiefComplaint || "No complaint recorded"}
          </p>
        )}
      </section>

      <PatientHealthSnapshot patient={patient} />

      {/* Actions */}
      <SidebarAccordion title="Actions" badge={actionBadgeCount}>
        <div className="grid gap-2">
          <Button
            onClick={startNewConsultation}
            startIcon={<Plus size={15} />}
            sx={primaryBtn}
          >
            New consultation
          </Button>

          <Button
            onClick={() => openConsultationDocument(null, "prescription")}
            startIcon={<Pill size={14} />}
            sx={secondaryBtn}
          >
            Send prescription
          </Button>

          <Button
            onClick={() => openConsultationDocument(null, "investigations")}
            startIcon={<FlaskConical size={14} />}
            sx={secondaryBtn}
          >
            Send investigations
          </Button>

          <Button
            onClick={() => setOpenPrescriptions(true)}
            startIcon={<FileText size={14} />}
            sx={secondaryBtn}
          >
            Previous prescriptions
          </Button>

          <Button
            onClick={openMediaGallery}
            startIcon={<Images size={14} />}
            sx={secondaryBtn}
          >
            Media gallery
            {mediaGalleryItems.length ? ` (${mediaGalleryItems.length})` : ""}
          </Button>

          <Button
            onClick={() => {
              setCustomPaymentOpen(true);
              setCustomPaymentError("");
            }}
            startIcon={<CreditCard size={14} />}
            sx={secondaryBtn}
          >
            Custom payment link
          </Button>

          <Button
            onClick={() => openReferralLetter()}
            startIcon={<FileText size={14} />}
            sx={secondaryBtn}
          >
            Referral letter
          </Button>

          {isPaid && (
            <Button
              onClick={() => setOpenSpecialistConsultation(true)}
              startIcon={<Stethoscope size={14} />}
              sx={secondaryBtn}
            >
              Specialist
            </Button>
          )}
          {isPaid && (
            <Button
              onClick={() => setReferralOpen(true)}
              startIcon={<Send size={14} />}
              sx={secondaryBtn}
            >
              Refer patient
            </Button>
          )}
        </div>
      </SidebarAccordion>

      {latestCostingRequest && (
        <SidebarAccordion
          title="Pharmacy costing"
          badge={
            normalizeCostingStatus(latestCostingRequest.status) === "uncosted"
              ? "Not costed"
              : normalizeCostingStatus(latestCostingRequest.status)
          }
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-950">
                {latestCostingRequest.pharmacyId?.name || "Pharmacy"}
              </p>
            </div>

            <span
              className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                normalizeCostingStatus(latestCostingRequest.status) ===
                "uncosted"
                  ? "bg-amber-50 text-amber-700"
                  : "bg-emerald-50 text-emerald-700"
              }`}
            >
              {normalizeCostingStatus(latestCostingRequest.status) ===
              "uncosted"
                ? "Not costed"
                : normalizeCostingStatus(latestCostingRequest.status)}
            </span>
          </div>

          <p className="mt-3 text-sm leading-6 text-slate-600">
            {normalizeCostingStatus(latestCostingRequest.status) === "uncosted"
              ? "Drug has not been costed yet."
              : `Prescription is ${normalizeCostingStatus(
                  latestCostingRequest.status
                )}.`}
          </p>

          <ul className="mt-3 space-y-1 text-sm text-slate-600">
            {(latestCostingRequest.drugs || []).slice(0, 4).map((drug, index) => (
              <li key={`${drug.name}-${index}`}>• {drug.name}</li>
            ))}
          </ul>

          {normalizeCostingStatus(latestCostingRequest.status) ===
            "uncosted" && (
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Button
                onClick={() =>
                  openPharmacyWhatsapp(latestCostingRequest.pharmacyId)
                }
                startIcon={<MessageCircle size={14} />}
                sx={secondaryBtn}
              >
                WhatsApp
              </Button>
              <Button
                onClick={() => openPharmacyCall(latestCostingRequest.pharmacyId)}
                startIcon={<Phone size={14} />}
                sx={secondaryBtn}
              >
                Call
              </Button>
            </div>
          )}
        </SidebarAccordion>
      )}

      {/* Consultations */}
      <section className="mt-5">
        <Accordion
          defaultExpanded={false}
          disableGutters
          elevation={0}
          sx={{
            border: "1px solid #e2e8f0",
            borderRadius: "16px",
            overflow: "hidden",
            background: "#fff",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ChevronDown size={18} />}
            sx={{
              px: 2,
              py: 1.4,
              "& .MuiAccordionSummary-content": {
                margin: 0,
              },
            }}
          >
            <div className="flex w-full items-center justify-between">
              <p className="text-sm font-semibold text-slate-950">
                Consultations
              </p>

              <span className="text-xs text-slate-400">
                {consultations.length}
              </span>
            </div>
          </AccordionSummary>

          <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
            <div className="space-y-3">
              {!consultations.length && (
                <p className="py-4 text-center text-sm text-slate-400">
                  No consultations yet.
                </p>
              )}

              {consultations.map((c) => (
                <div
                  key={c._id}
                  className="rounded-xl border border-slate-200 bg-white p-3"
                >
                  <p className="text-sm font-medium text-slate-950">
                    {c.workingDiagnosis || "No diagnosis"}
                  </p>

                  <p className="mt-1 text-xs text-slate-400">
                    {new Date(c.date).toLocaleString([], {
                      day: "numeric",
                      month: "numeric",
                      year: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })}
                  </p>

                  {c.followUpReminderEnabled !== false && c.followUpDate && (
                    <div className="mt-3 rounded-lg border border-emerald-100 bg-emerald-50 p-2">
                      <p className="text-xs font-semibold text-emerald-800">
                        Follow-up: {formatDate(c.followUpDate)}
                      </p>
                      <p className="mt-1 text-[11px] leading-4 text-emerald-700">
                        Email reminder is sent the day before and on the day.
                      </p>
                    </div>
                  )}

                  <p className="mt-3 line-clamp-4 whitespace-pre-wrap text-sm leading-6 text-slate-600">
                    {c.clientHistory || "—"}
                  </p>

                  {c.medications?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                        Medications
                      </p>

                      <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-slate-600">
                        {c.medications.map((m, i) => (
                          <li key={i}>{m}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {c.investigations?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                        Investigations
                      </p>

                      <div className="mt-2 space-y-2">
                        {c.investigations.map((inv, i) => (
                          <div
                            key={i}
                            className="rounded-lg border border-slate-200 bg-slate-50 p-2"
                          >
                            <p className="text-sm font-medium text-slate-800">
                              {inv.name || "Unnamed"}
                            </p>

                            {inv.result && (
                              <p className="mt-1 text-xs text-slate-500">
                                Result: {inv.result}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => copySummary(c)}
                      className="inline-flex items-center gap-2 text-xs font-medium text-slate-700 hover:underline"
                    >
                      <Copy size={13} />
                      Copy summary
                    </button>

                    {c.medications?.length > 0 && (
                      <button
                        type="button"
                        onClick={() => openConsultationDocument(c, "prescription")}
                        className="inline-flex items-center gap-2 text-xs font-medium text-[#075E54] hover:underline"
                      >
                        <Pill size={13} />
                        Resend prescription
                      </button>
                    )}

                    {c.investigations?.length > 0 && (
                      <button
                        type="button"
                        onClick={() =>
                          openConsultationDocument(c, "investigations")
                        }
                        className="inline-flex items-center gap-2 text-xs font-medium text-[#075E54] hover:underline"
                      >
                        <FlaskConical size={13} />
                        Resend investigations
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => openReferralLetter(c)}
                      className="inline-flex items-center gap-2 text-xs font-medium text-[#075E54] hover:underline"
                    >
                      <FileText size={13} />
                      Referral letter
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </AccordionDetails>
        </Accordion>
      </section>

      <SidebarAccordion title="Prescriptions" badge={prescriptionHistory.length}>
        <div className="space-y-3">
          {!prescriptionHistory.length && (
            <p className="py-4 text-center text-sm text-slate-400">
              No prescriptions yet.
            </p>
          )}

          {prescriptionHistory.map((item) => (
            <div
              key={item.key}
              className="rounded-xl border border-slate-200 bg-white p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-950">
                    {item.diagnosis || "Prescription"}
                  </p>
                  <p className="mt-1 text-xs text-slate-400">
                    {formatDate(item.date)} · {item.doctorName}
                  </p>
                </div>

                <span className="shrink-0 rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-500">
                  {item.source}
                </span>
              </div>

              <ul className="mt-3 space-y-1 text-sm text-slate-600">
                {item.drugs.slice(0, 4).map((drug, index) => (
                  <li key={`${item.key}-${index}`}>• {drug}</li>
                ))}
              </ul>

              {item.drugs.length > 4 && (
                <p className="mt-2 text-xs text-slate-400">
                  +{item.drugs.length - 4} more medicine
                  {item.drugs.length - 4 === 1 ? "" : "s"}
                </p>
              )}

              <button
                type="button"
                onClick={() => openHistoryDocument(item)}
                className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-[#075E54] hover:underline"
              >
                <Pill size={13} />
                Preview and resend
              </button>
            </div>
          ))}
        </div>
      </SidebarAccordion>

      <SidebarAccordion
        title="Investigations"
        badge={investigationHistory.length}
      >
        <div className="space-y-3">
          {!investigationHistory.length && (
            <p className="py-4 text-center text-sm text-slate-400">
              No investigations yet.
            </p>
          )}

          {investigationHistory.map((item) => (
            <div
              key={item.key}
              className="rounded-xl border border-slate-200 bg-white p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-950">
                    {item.diagnosis || "Investigation request"}
                  </p>
                  <p className="mt-1 text-xs text-slate-400">
                    {formatDate(item.date)} · {item.doctorName}
                  </p>
                </div>

                <span className="shrink-0 rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-500">
                  {item.source}
                </span>
              </div>

              <div className="mt-3 space-y-2">
                {item.investigations.slice(0, 3).map((investigation, index) => (
                  <div
                    key={`${item.key}-${index}`}
                    className="rounded-lg border border-slate-200 bg-slate-50 p-2"
                  >
                    <p className="text-sm font-medium text-slate-800">
                      {investigation.name || "Unnamed investigation"}
                    </p>
                    {(investigation.result || investigation.comment) && (
                      <p className="mt-1 line-clamp-2 text-xs text-slate-500">
                        {investigation.result || investigation.comment}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {item.investigations.length > 3 && (
                <p className="mt-2 text-xs text-slate-400">
                  +{item.investigations.length - 3} more investigation
                  {item.investigations.length - 3 === 1 ? "" : "s"}
                </p>
              )}

              <button
                type="button"
                onClick={() => openHistoryDocument(item)}
                className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-[#075E54] hover:underline"
              >
                <FlaskConical size={13} />
                Preview and resend
              </button>
            </div>
          ))}
        </div>
      </SidebarAccordion>

      {/* Referral letters */}
      <section className="mt-5">
        <Accordion
          defaultExpanded={false}
          disableGutters
          elevation={0}
          sx={{
            border: "1px solid #e2e8f0",
            borderRadius: "16px",
            overflow: "hidden",
            background: "#fff",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ChevronDown size={18} />}
            sx={{
              px: 2,
              py: 1.4,
              "& .AccordionSummary-content": {
                margin: 0,
              },
              "& .MuiAccordionSummary-content": {
                margin: 0,
              },
            }}
          >
            <div className="flex w-full items-center justify-between">
              <p className="text-sm font-semibold text-slate-950">
                Referral letters
              </p>

              <span className="text-xs text-slate-400">
                {referralLetters.length}
              </span>
            </div>
          </AccordionSummary>

          <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
            <div className="space-y-3">
              {!referralLetters.length && (
                <p className="py-4 text-center text-sm text-slate-400">
                  No referral letters yet.
                </p>
              )}

              {referralLetters.map((letter) => (
                <div
                  key={letter._id || `${letter.sentAt}-${letter.facility}`}
                  className="rounded-xl border border-slate-200 bg-white p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-950">
                        {letter.provisionalDiagnosis ||
                          letter.reasonForReferral ||
                          "Referral letter"}
                      </p>

                      <p className="mt-1 text-xs text-slate-400">
                        Sent {formatDate(letter.sentAt || letter.date)}
                      </p>
                    </div>

                    <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700">
                      {letter.urgency || "Routine"}
                    </span>
                  </div>

                  <div className="mt-3 space-y-2 text-sm text-slate-600">
                    <p>
                      <span className="font-medium text-slate-800">To:</span>{" "}
                      {letter.recipientName || "Not recorded"}
                    </p>
                    <p>
                      <span className="font-medium text-slate-800">
                        Facility:
                      </span>{" "}
                      {letter.facility || "Not recorded"}
                    </p>
                    <p className="line-clamp-3 whitespace-pre-wrap">
                      <span className="font-medium text-slate-800">
                        Reason:
                      </span>{" "}
                      {letter.reasonForReferral || "Not recorded"}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => openSavedReferralLetter(letter)}
                    className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-[#075E54] hover:underline"
                  >
                    <FileText size={13} />
                    Edit and resend
                  </button>
                </div>
              ))}
            </div>
          </AccordionDetails>
        </Accordion>
      </section>

      {draftStatus && (
        <p className="mt-4 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-500">
          {draftStatus}
        </p>
      )}

      {customPaymentOpen && (
        <div className="fixed inset-0 z-[76] flex items-end bg-slate-950/45 px-4 py-4 sm:items-center sm:justify-center">
          <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl sm:p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-base font-semibold text-slate-950">
                  Send payment link
                </p>
                <p className="mt-1 text-sm leading-6 text-slate-500">
                  Enter the amount and what the patient is paying for.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setCustomPaymentOpen(false)}
                disabled={customPaymentSending}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                aria-label="Close payment link modal"
              >
                <X size={17} />
              </button>
            </div>

            <div className="mt-5 space-y-3">
              <InputBase
                value={customPaymentAmount}
                onChange={(event) => setCustomPaymentAmount(event.target.value)}
                placeholder="Amount, e.g. 2500"
                type="number"
                inputProps={{ min: 1 }}
                fullWidth
                sx={inputSx}
                className={inputClass}
              />

              <InputBase
                value={customPaymentDescription}
                onChange={(event) =>
                  setCustomPaymentDescription(event.target.value)
                }
                placeholder="Description, e.g. Home testing kit"
                fullWidth
                multiline
                minRows={3}
                sx={inputSx}
                className={inputClass}
              />

              {customPaymentAmount && (
                <p className="rounded-xl border border-emerald-100 bg-emerald-50 p-3 text-sm font-semibold text-emerald-800">
                  Patient will receive a secure link for{" "}
                  {formatNaira(customPaymentAmount)}.
                </p>
              )}

              {customPaymentError && (
                <p className="rounded-xl border border-amber-100 bg-amber-50 p-3 text-sm font-medium text-amber-800">
                  {customPaymentError}
                </p>
              )}

              <div className="grid grid-cols-2 gap-3 pt-1">
                <Button
                  onClick={() => setCustomPaymentOpen(false)}
                  disabled={customPaymentSending}
                  sx={secondaryBtn}
                >
                  Close
                </Button>

                <Button
                  onClick={sendCustomPaymentLink}
                  disabled={customPaymentSending}
                  startIcon={<CreditCard size={15} />}
                  sx={primaryBtn}
                >
                  {customPaymentSending ? "Sending..." : "Send link"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {mediaGalleryOpen && (
        <div className="fixed inset-0 z-[75] flex items-end bg-slate-950/55 sm:items-center sm:justify-center">
          <div className="max-h-[94dvh] w-full overflow-y-auto rounded-t-3xl bg-white p-4 shadow-2xl sm:max-w-5xl sm:rounded-3xl sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-400">
                  <Images size={14} />
                  Media gallery
                </p>
                <h4 className="mt-1 text-lg font-semibold text-slate-950">
                  Images and videos
                </h4>
                <p className="mt-1 text-sm text-slate-500">
                  {mediaGalleryItems.length} item
                  {mediaGalleryItems.length === 1 ? "" : "s"} from this patient
                  conversation.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setMediaGalleryOpen(false)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50"
              >
                <X size={16} />
              </button>
            </div>

            {activeMedia ? (
              <>
                <div className="mt-5 overflow-hidden rounded-2xl bg-slate-950">
                  <div className="relative flex min-h-[320px] items-center justify-center sm:min-h-[520px]">
                    {mediaGalleryItems.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={showPreviousMedia}
                          className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-900 shadow-sm hover:bg-white"
                        >
                          <ChevronLeft size={20} />
                        </button>

                        <button
                          type="button"
                          onClick={showNextMedia}
                          className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-900 shadow-sm hover:bg-white"
                        >
                          <ChevronRight size={20} />
                        </button>
                      </>
                    )}

                    {activeMedia.kind === "image" ? (
                      <img
                        src={activeMedia.url}
                        alt="Patient media"
                        className="max-h-[68dvh] w-full object-contain"
                      />
                    ) : (
                      <video
                        key={activeMedia.id}
                        controls
                        playsInline
                        preload="metadata"
                        className="max-h-[68dvh] w-full bg-black"
                      >
                        <source
                          src={activeMedia.url}
                          type={activeMedia.type || undefined}
                        />
                        Your browser does not support video playback.
                      </video>
                    )}
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                  <span>
                    {activeMedia.sender === "doctor" ? "Doctor" : "Patient"} ·{" "}
                    {formatDateTime(activeMedia.timestamp)}
                  </span>
                  <span>
                    {activeMediaIndex + 1} of {mediaGalleryItems.length}
                  </span>
                </div>

                {activeMedia.caption && (
                  <p className="mt-2 line-clamp-3 whitespace-pre-wrap text-sm leading-6 text-slate-600">
                    {activeMedia.caption}
                  </p>
                )}

                <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
                  {mediaGalleryItems.map((item, index) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setActiveMediaIndex(index)}
                      className={`h-16 w-20 shrink-0 overflow-hidden rounded-xl border bg-slate-100 transition ${
                        index === activeMediaIndex
                          ? "border-[#075E54] ring-2 ring-[#075E54]/20"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      {item.kind === "image" ? (
                        <img
                          src={item.url}
                          alt="Media thumbnail"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="relative h-full w-full bg-slate-950">
                          <video
                            src={item.url}
                            muted
                            playsInline
                            preload="metadata"
                            className="h-full w-full object-cover opacity-80"
                          />
                          <span className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold text-white">
                            Video
                          </span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <p className="mt-5 rounded-2xl bg-slate-50 p-4 text-center text-sm text-slate-500">
                No images or videos have been sent in this conversation yet.
              </p>
            )}
          </div>
        </div>
      )}

      {referralLetterOpen && referralLetter && (
        <div className="fixed inset-0 z-[80] flex items-end bg-slate-950/45 sm:items-center sm:justify-center">
          <div className="max-h-[92dvh] w-full overflow-y-auto rounded-t-3xl bg-white p-4 shadow-2xl sm:max-w-3xl sm:rounded-3xl sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  PDF to patient email
                </p>
                <h4 className="mt-1 text-lg font-semibold text-slate-950">
                  Referral letter
                </h4>
                <p className="mt-1 text-sm text-slate-500">
                  Prefilled from consultation notes. Edit before sending to{" "}
                  {patient.email || "patient email"}.
                </p>
              </div>

              <button
                type="button"
                onClick={closeReferralLetter}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50"
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {[
                ["date", "Date", "date"],
                ["patientName", "Patient full name"],
                ["age", "Age"],
                ["sex", "Sex"],
                ["phoneNumber", "Phone number"],
                ["address", "Address"],
                ["recipientName", "Receiving doctor / consultant"],
                ["specialty", "Specialty / department"],
                ["facility", "Hospital / facility"],
                ["doctorName", "Referring doctor"],
              ].map(([key, label, type]) => (
                <div key={key}>
                  <label className="mb-2 block text-xs font-medium text-slate-500">
                    {label}
                  </label>
                  <InputBase
                    type={type || "text"}
                    value={referralLetter[key] || ""}
                    onChange={(e) =>
                      updateReferralLetter(key, e.target.value)
                    }
                    fullWidth
                    sx={inputSx}
                    className={inputClass}
                  />
                </div>
              ))}

              <div>
                <label className="mb-2 block text-xs font-medium text-slate-500">
                  Urgency
                </label>
                <select
                  value={referralLetter.urgency || "Routine"}
                  onChange={(e) =>
                    updateReferralLetter("urgency", e.target.value)
                  }
                  className="h-[45px] w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none"
                >
                  <option value="Routine">Routine</option>
                  <option value="Urgent">Urgent</option>
                  <option value="Emergency">Emergency</option>
                </select>
              </div>
            </div>

            <div className="mt-5 space-y-4">
              {[
                ["reasonForReferral", "Reason for referral", 3],
                ["presentingComplaint", "Presenting complaint", 3],
                ["relevantHistory", "Relevant history", 4],
                ["provisionalDiagnosis", "Provisional diagnosis", 3],
                [
                  "investigations",
                  "Relevant investigations and results",
                  4,
                ],
                ["treatmentProvided", "Treatment already provided", 3],
                ["currentMedications", "Current medications", 4],
                ["knownAllergies", "Known allergies", 3],
                [
                  "medicalHistory",
                  "Relevant medical / surgical history",
                  3,
                ],
              ].map(([key, label, rows]) => (
                <div key={key}>
                  <label className="mb-2 block text-xs font-medium text-slate-500">
                    {label}
                  </label>
                  <InputBase
                    multiline
                    rows={rows}
                    value={referralLetter[key] || ""}
                    onChange={(e) =>
                      updateReferralLetter(key, e.target.value)
                    }
                    fullWidth
                    sx={inputSx}
                    className={inputClass}
                  />
                </div>
              ))}
            </div>

            <div className="sticky bottom-0 mt-5 grid gap-3 border-t border-slate-200 bg-white pt-4 sm:grid-cols-2">
              <Button
                onClick={closeReferralLetter}
                variant="outlined"
                sx={{
                  borderColor: "#e2e8f0",
                  color: "#334155",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 500,
                  py: 1.1,
                  boxShadow: "none",
                }}
              >
                Cancel
              </Button>

              <Button
                onClick={sendReferralLetter}
                disabled={sendingReferralLetter}
                startIcon={<Send size={16} />}
                sx={{
                  bgcolor: "#075E54",
                  color: "white",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.1,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#064b43", boxShadow: "none" },
                  "&:disabled": {
                    bgcolor: "#e2e8f0",
                    color: "#64748b",
                  },
                }}
              >
                {sendingReferralLetter ? "Sending..." : "Send PDF to email"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {documentModal && (
        <div className="fixed inset-0 z-[85] flex items-end bg-slate-950/45 sm:items-center sm:justify-center">
          <div className="max-h-[92dvh] w-full overflow-y-auto rounded-t-3xl bg-white p-4 shadow-2xl sm:max-w-4xl sm:rounded-3xl sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-400">
                  <Eye size={14} />
                  Preview and edit
                </p>
                <h4 className="mt-1 text-lg font-semibold text-slate-950">
                  {documentTitle}
                </h4>
                <p className="mt-1 text-sm text-slate-500">
                  Check the details before sending the PDF to{" "}
                  {patient.email || "patient email"}.
                </p>
              </div>

              <button
                type="button"
                onClick={closeConsultationDocument}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50"
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-5 grid gap-5 lg:grid-cols-[1.05fr_0.95fr]">
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-xs font-medium text-slate-500">
                    Diagnosis or reason
                  </label>
                  <InputBase
                    placeholder="Diagnosis or reason for this document"
                    value={documentModal.diagnosis || ""}
                    onChange={(e) =>
                      updateDocumentDraft("diagnosis", e.target.value)
                    }
                    fullWidth
                    sx={inputSx}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-xs font-medium text-slate-500">
                    Note on PDF
                  </label>
                  <InputBase
                    multiline
                    rows={3}
                    value={documentModal.notes || ""}
                    onChange={(e) =>
                      updateDocumentDraft("notes", e.target.value)
                    }
                    fullWidth
                    sx={inputSx}
                    className={inputClass}
                  />
                </div>

                {documentIsPrescription ? (
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <label className="text-xs font-medium text-slate-500">
                        Medicines
                      </label>

                      <button
                        type="button"
                        onClick={() =>
                          updateDocumentDraft("drugs", [
                            ...(documentModal.drugs || []),
                            "",
                          ])
                        }
                        className="text-xs font-medium text-slate-700 hover:underline"
                      >
                        Add medicine
                      </button>
                    </div>

                    <div className="space-y-2">
                      {(documentModal.drugs || []).map((drug, index) => (
                        <div key={index} className="flex gap-2">
                          <InputBase
                            value={drug}
                            placeholder={`Medicine ${index + 1}`}
                            onChange={(e) =>
                              updateDocumentDrug(index, e.target.value)
                            }
                            fullWidth
                            sx={inputSx}
                            className={inputClass}
                          />

                          <button
                            type="button"
                            onClick={() =>
                              updateDocumentDraft(
                                "drugs",
                                (documentModal.drugs || []).filter(
                                  (_, idx) => idx !== index
                                )
                              )
                            }
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-red-500 hover:bg-red-50"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-slate-700">
                            Send to pharmacy
                          </p>
                          <p className="mt-1 text-xs leading-5 text-slate-400">
                            Ask a pharmacy to cost these medicines after the PDF
                            is sent to the patient.
                          </p>
                        </div>

                        <Toggle
                          checked={documentSendToPharmacy}
                          onClick={() =>
                            setDocumentSendToPharmacy((prev) => {
                              const next = !prev;
                              if (!next) {
                                setDocumentSelectedPharmacy(null);
                                setDocumentPharmacySearch("");
                              }
                              return next;
                            })
                          }
                        />
                      </div>

                      {documentSendToPharmacy && (
                        <div className="mt-4 space-y-4">
                          <InputBase
                            placeholder="Search pharmacy"
                            value={documentPharmacySearch}
                            onChange={(e) =>
                              setDocumentPharmacySearch(e.target.value)
                            }
                            fullWidth
                            sx={inputSx}
                            className={inputClass}
                          />

                          <div className="max-h-48 space-y-2 overflow-y-auto">
                            {filteredDocumentPharmacies.map((pharmacy) => (
                              <button
                                key={pharmacy._id}
                                type="button"
                                onClick={() =>
                                  setDocumentSelectedPharmacy(pharmacy)
                                }
                                className={`w-full rounded-xl border p-3 text-left transition ${
                                  documentSelectedPharmacy?._id === pharmacy._id
                                    ? "border-[#075E54] bg-slate-50"
                                    : "border-slate-200 bg-white hover:bg-slate-50"
                                }`}
                              >
                                <p className="text-sm font-medium text-slate-900">
                                  {pharmacy.name}
                                </p>
                                <p className="mt-1 text-xs text-slate-400">
                                  {pharmacy.location?.address || "No address"}
                                </p>
                              </button>
                            ))}
                          </div>

                          {documentSelectedPharmacy && (
                            <p className="text-xs text-slate-500">
                              Selected:{" "}
                              <span className="font-medium text-slate-800">
                                {documentSelectedPharmacy.name}
                              </span>
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <label className="text-xs font-medium text-slate-500">
                        Investigations
                      </label>

                      <button
                        type="button"
                        onClick={() =>
                          updateDocumentDraft("investigations", [
                            ...(documentModal.investigations || []),
                            { name: "", result: "", comment: "", date: "" },
                          ])
                        }
                        className="text-xs font-medium text-slate-700 hover:underline"
                      >
                        Add test
                      </button>
                    </div>

                    <div className="space-y-3">
                      {(documentModal.investigations || []).map(
                        (investigation, index) => (
                          <div
                            key={index}
                            className="rounded-xl border border-slate-200 bg-slate-50 p-3"
                          >
                            <div className="mb-3 flex items-center justify-between">
                              <p className="text-xs text-slate-500">
                                Investigation {index + 1}
                              </p>

                              <button
                                type="button"
                                onClick={() =>
                                  updateDocumentDraft(
                                    "investigations",
                                    (documentModal.investigations || []).filter(
                                      (_, idx) => idx !== index
                                    )
                                  )
                                }
                                className="text-red-500"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>

                            <div className="space-y-2">
                              <InputBase
                                placeholder="Test name"
                                value={investigation.name || ""}
                                onChange={(e) =>
                                  updateDocumentInvestigation(
                                    index,
                                    "name",
                                    e.target.value
                                  )
                                }
                                fullWidth
                                sx={inputSx}
                                className={inputClass}
                              />

                              <InputBase
                                placeholder="Result, if any"
                                value={investigation.result || ""}
                                onChange={(e) =>
                                  updateDocumentInvestigation(
                                    index,
                                    "result",
                                    e.target.value
                                  )
                                }
                                fullWidth
                                sx={inputSx}
                                className={inputClass}
                              />

                              <InputBase
                                placeholder="Doctor comment"
                                multiline
                                rows={3}
                                value={investigation.comment || ""}
                                onChange={(e) =>
                                  updateDocumentInvestigation(
                                    index,
                                    "comment",
                                    e.target.value
                                  )
                                }
                                fullWidth
                                sx={inputSx}
                                className={inputClass}
                              />

                              <InputBase
                                type="date"
                                value={investigation.date || ""}
                                onChange={(e) =>
                                  updateDocumentInvestigation(
                                    index,
                                    "date",
                                    e.target.value
                                  )
                                }
                                fullWidth
                                sx={inputSx}
                                className={inputClass}
                              />
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  Email PDF preview
                </p>

                <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4 text-sm leading-6 text-slate-700">
                  <div className="border-b border-slate-200 pb-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#075E54]">
                      Zaviramed
                    </p>
                    <h5 className="mt-1 text-base font-semibold text-slate-950">
                      {documentTitle}
                    </h5>
                  </div>

                  <div className="mt-4 space-y-2">
                    <p>
                      <span className="font-medium text-slate-900">
                        Patient:
                      </span>{" "}
                      {patient.name || "Patient"}
                    </p>
                    <p>
                      <span className="font-medium text-slate-900">
                        Email:
                      </span>{" "}
                      {patient.email || "No email address"}
                    </p>
                    <p>
                      <span className="font-medium text-slate-900">
                        Date:
                      </span>{" "}
                      {formatDate(documentModal.consultationDate)}
                    </p>
                    <p>
                      <span className="font-medium text-slate-900">
                        Doctor:
                      </span>{" "}
                      {documentModal.doctorName || "Zaviramed Doctor"}
                    </p>
                    <p>
                      <span className="font-medium text-slate-900">
                        Diagnosis:
                      </span>{" "}
                      {documentModal.diagnosis || "Not specified"}
                    </p>
                  </div>

                  <div className="mt-4">
                    <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">
                      {documentIsPrescription ? "Medicines" : "Investigations"}
                    </p>

                    {documentIsPrescription ? (
                      cleanList(documentModal.drugs).length ? (
                        <ol className="list-decimal space-y-1 pl-4">
                          {cleanList(documentModal.drugs).map((drug, index) => (
                            <li key={index}>{drug}</li>
                          ))}
                        </ol>
                      ) : (
                        <p className="text-slate-400">No medicine added.</p>
                      )
                    ) : cleanInvestigationRows(documentModal.investigations)
                        .length ? (
                      <ol className="list-decimal space-y-2 pl-4">
                        {cleanInvestigationRows(
                          documentModal.investigations
                        ).map((investigation, index) => {
                          const parts = [];
                          if (investigation.name) parts.push(investigation.name);
                          if (investigation.result) {
                            parts.push(`Result: ${investigation.result}`);
                          }
                          if (investigation.comment) {
                            parts.push(`Comment: ${investigation.comment}`);
                          }
                          if (investigation.date) {
                            parts.push(`Date: ${investigation.date}`);
                          }

                          return <li key={index}>{parts.join(" | ")}</li>;
                        })}
                      </ol>
                    ) : (
                      <p className="text-slate-400">No investigation added.</p>
                    )}
                  </div>

                  {documentModal.notes && (
                    <div className="mt-4 rounded-xl bg-slate-50 p-3">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                        Note
                      </p>
                      <p className="mt-2 whitespace-pre-wrap">
                        {documentModal.notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 mt-5 grid gap-3 border-t border-slate-200 bg-white pt-4 sm:grid-cols-2">
              <Button
                onClick={closeConsultationDocument}
                variant="outlined"
                sx={{
                  borderColor: "#e2e8f0",
                  color: "#334155",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 500,
                  py: 1.1,
                  boxShadow: "none",
                }}
              >
                Cancel
              </Button>

              <Button
                onClick={requestSendConsultationDocument}
                disabled={sendingDocument || !documentCanSend}
                startIcon={<Send size={16} />}
                sx={{
                  bgcolor: "#075E54",
                  color: "white",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.1,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#064b43", boxShadow: "none" },
                  "&:disabled": {
                    bgcolor: "#e2e8f0",
                    color: "#64748b",
                  },
                }}
              >
                {sendingDocument ? "Sending..." : `Send ${documentTitle} PDF`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {documentModal && documentConfirmOpen && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-slate-950/50 px-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  Confirm before sending
                </p>
                <h4 className="mt-1 text-lg font-semibold text-slate-950">
                  {documentIsPrescription
                    ? documentSendToPharmacy
                      ? "Send prescription with pharmacy?"
                      : "Send prescription without pharmacy?"
                    : "Send investigations?"}
                </h4>
              </div>

              <button
                type="button"
                onClick={() => setDocumentConfirmOpen(false)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50"
              >
                <X size={16} />
              </button>
            </div>

            <p className="mt-4 text-sm leading-6 text-slate-600">
              {documentIsPrescription
                ? documentSendToPharmacy
                  ? `This will email the prescription PDF to ${
                      patient.email || "the patient"
                    } and send the medicines to ${
                      documentSelectedPharmacy?.name || "the selected pharmacy"
                    } for costing.`
                  : `This will email the prescription PDF to ${
                      patient.email || "the patient"
                    } only. No pharmacy will receive it for costing.`
                : `This will email the investigations PDF to ${
                    patient.email || "the patient"
                  }.`}
            </p>

            {documentIsPrescription && (
              <div
                className={`mt-4 rounded-2xl border p-3 text-sm ${
                  documentSendToPharmacy
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-amber-200 bg-amber-50 text-amber-800"
                }`}
              >
                {documentSendToPharmacy
                  ? "Pharmacy costing is on."
                  : "Pharmacy costing is off. This is okay if the doctor only wants the patient PDF sent."}
              </div>
            )}

            <div className="mt-5 grid grid-cols-2 gap-3">
              <Button
                onClick={() => setDocumentConfirmOpen(false)}
                variant="outlined"
                sx={{
                  borderColor: "#e2e8f0",
                  color: "#334155",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 500,
                  py: 1.1,
                  boxShadow: "none",
                }}
              >
                Go back
              </Button>

              <Button
                onClick={sendConsultationDocument}
                disabled={sendingDocument}
                startIcon={<Send size={16} />}
                sx={{
                  bgcolor: "#075E54",
                  color: "white",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.1,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#064b43", boxShadow: "none" },
                  "&:disabled": {
                    bgcolor: "#e2e8f0",
                    color: "#64748b",
                  },
                }}
              >
                {sendingDocument ? "Sending..." : "Yes, send"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Active consultation */}
      {activeConsultation && (
        <section className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-slate-950">
                Active consultation
              </h4>
              <p className="mt-1 text-xs text-slate-500">
                Document this encounter.
              </p>
            </div>

            <button
              type="button"
              onClick={cancelActiveConsultation}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50"
            >
              <X size={16} />
            </button>
          </div>

          <div className="mt-5 space-y-5">
            {/* Diagnosis */}
            <div>
              <label className="mb-2 block text-xs font-medium text-slate-500">
                Working diagnosis
              </label>

              <InputBase
                placeholder="Enter working diagnosis"
                value={activeConsultation.workingDiagnosis}
                onChange={(e) =>
                  updateField("workingDiagnosis", e.target.value)
                }
                fullWidth
                sx={inputSx}
                className={inputClass}
              />
            </div>

            {/* Notes */}
            <div>
              <label className="mb-2 block text-xs font-medium text-slate-500">
                Clinical notes
              </label>

              <InputBase
                multiline
                rows={4}
                placeholder="History, findings, impression, plan"
                value={activeConsultation.clientHistory}
                onChange={(e) => updateField("clientHistory", e.target.value)}
                fullWidth
                sx={inputSx}
                className={inputClass}
              />
            </div>

            {/* Follow-up reminder */}
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex gap-3">

                  <div>
                    <p className="text-sm font-semibold text-slate-800">
                      Follow-up reminder
                    </p>
                   
                  </div>
                </div>

                <Toggle
                  checked={activeConsultation.followUpReminderEnabled !== false}
                  onClick={() =>
                    updateField(
                      "followUpReminderEnabled",
                      activeConsultation.followUpReminderEnabled === false
                    )
                  }
                />
              </div>

              {activeConsultation.followUpReminderEnabled !== false && (
                <div className="mt-4">
                

                  <InputBase
                    type="date"
                    value={
                      activeConsultation.followUpDate || addDaysInputDate(7)
                    }
                    onChange={(e) =>
                      updateField("followUpDate", e.target.value)
                    }
                    fullWidth
                    inputProps={{ min: todayInputDate() }}
                    sx={inputSx}
                    className={inputClass}
                  />
                </div>
              )}
            </div>

            {/* Investigations */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="text-xs font-medium text-slate-500">
                  Investigations
                </label>

                <button
                  type="button"
                  onClick={() =>
                    updateField("investigations", [
                      ...(activeConsultation.investigations || []),
                      { name: "", result: "", comment: "", date: "" },
                    ])
                  }
                  className="text-xs font-medium text-slate-700 hover:underline"
                >
                  Add
                </button>
              </div>

              <div className="space-y-3">
                {(activeConsultation.investigations || []).map((inv, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-slate-200 bg-slate-50 p-3"
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-xs text-slate-500">
                        Investigation {i + 1}
                      </p>

                      <button
                        type="button"
                        onClick={() =>
                          updateField(
                            "investigations",
                            activeConsultation.investigations.filter(
                              (_, idx) => idx !== i
                            )
                          )
                        }
                        className="text-red-500"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    <div className="space-y-2">
                      <InputBase
                        placeholder="Test name"
                        value={inv.name}
                        onChange={(e) =>
                          updateInvestigation(i, "name", e.target.value)
                        }
                        fullWidth
                        sx={inputSx}
                        className={inputClass}
                      />

                      <InputBase
                        placeholder="Result"
                        value={inv.result}
                        onChange={(e) =>
                          updateInvestigation(i, "result", e.target.value)
                        }
                        fullWidth
                        sx={inputSx}
                        className={inputClass}
                      />

                      <InputBase
                        placeholder="Comment"
                        multiline
                        rows={3}
                        value={inv.comment}
                        onChange={(e) =>
                          updateInvestigation(i, "comment", e.target.value)
                        }
                        fullWidth
                        sx={inputSx}
                        className={inputClass}
                      />

                      <InputBase
                        type="date"
                        value={inv.date}
                        onChange={(e) =>
                          updateInvestigation(i, "date", e.target.value)
                        }
                        fullWidth
                        sx={inputSx}
                        className={inputClass}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Medications */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="text-xs font-medium text-slate-500">
                  Medications
                </label>

                <button
                  type="button"
                  onClick={() =>
                    updateField("medications", [
                      ...(activeConsultation.medications || []),
                      "",
                    ])
                  }
                  className="text-xs font-medium text-slate-700 hover:underline"
                >
                  Add
                </button>
              </div>

              <div className="space-y-2">
                {(activeConsultation.medications || []).map((med, i) => (
                  <div key={i} className="flex gap-2">
                    <InputBase
                      value={med}
                      placeholder={`Medication ${i + 1}`}
                      onChange={(e) => {
                        const copy = [...activeConsultation.medications];
                        copy[i] = e.target.value;
                        updateField("medications", copy);
                      }}
                      fullWidth
                      sx={inputSx}
                      className={inputClass}
                    />

                    <button
                      type="button"
                      onClick={() =>
                        updateField(
                          "medications",
                          activeConsultation.medications.filter(
                            (_, idx) => idx !== i
                          )
                        )
                      }
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-red-500 hover:bg-red-50"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Summary */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs font-medium text-slate-500">
                  Consultation summary
                </p>

                <button
                  type="button"
                  onClick={() => copySummary(activeConsultation)}
                  className="text-xs font-medium text-slate-700 hover:underline"
                >
                  Copy
                </button>
              </div>

              <textarea
                readOnly
                value={generateSummary(activeConsultation)}
                className="min-h-[180px] w-full resize-none rounded-xl border border-slate-200 bg-white p-3 text-xs leading-5 text-slate-700 outline-none"
              />
            </div>

            {/* Document / pharmacy options */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-slate-700">
                  Send prescription
                </p>

                <Toggle
                  checked={sendPatientPrescription}
                  onClick={() => setSendPatientPrescription((prev) => !prev)}
                />
              </div>

              <div className="mt-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    Send investigations
                  </p>
                  {sendPatientInvestigations && (
                    <p className="mt-1 text-xs text-slate-400">
                      {activeInvestigationCount
                        ? `${activeInvestigationCount} item${
                            activeInvestigationCount === 1 ? "" : "s"
                          } will be emailed as PDF`
                        : "Add at least one investigation above"}
                    </p>
                  )}
                </div>

                <Toggle
                  checked={sendPatientInvestigations}
                  onClick={() =>
                    setSendPatientInvestigations((prev) => !prev)
                  }
                />
              </div>

              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm font-medium text-slate-700">
                  Pharmacy costing
                </p>

                <Toggle
                  checked={sendForCosting}
                  onClick={() => setSendForCosting((prev) => !prev)}
                />
              </div>

              {sendForCosting && (
                <div className="mt-5 space-y-4">
                  <InputBase
                    placeholder="Search pharmacy"
                    value={searchPharmacy}
                    onChange={(e) => setSearchPharmacy(e.target.value)}
                    fullWidth
                    sx={inputSx}
                    className={inputClass}
                  />

                  <div className="max-h-48 space-y-2 overflow-y-auto">
                    {filteredPharmacies.map((p) => (
                      <button
                        key={p._id}
                        type="button"
                        onClick={() => setSelectedPharmacy(p)}
                        className={`w-full rounded-xl border p-3 text-left transition ${
                          selectedPharmacy?._id === p._id
                            ? "border-[#075E54] bg-slate-50"
                            : "border-slate-200 bg-white hover:bg-slate-50"
                        }`}
                      >
                        <p className="text-sm font-medium text-slate-900">
                          {p.name}
                        </p>
                        <p className="mt-1 text-xs text-slate-400">
                          {p.location?.address || "No address"}
                        </p>
                      </button>
                    ))}
                  </div>

                  {selectedPharmacy && (
                    <p className="text-xs text-slate-500">
                      Selected:{" "}
                      <span className="font-medium text-slate-800">
                        {selectedPharmacy.name}
                      </span>
                    </p>
                  )}

                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <p className="mb-2 text-xs text-slate-400">
                      Drugs to be costed
                    </p>

                    <ul className="space-y-1 text-sm text-slate-700">
                      {(activeConsultation.medications || [])
                        .filter(Boolean)
                        .map((m, i) => (
                          <li key={i}>• {m}</li>
                        ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={clearDraft}
                variant="outlined"
                sx={{
                  borderColor: "#e2e8f0",
                  color: "#334155",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 500,
                  py: 1.1,
                  boxShadow: "none",
                }}
              >
                Clear
              </Button>

              <Button
                onClick={completeConsultation}
                disabled={saving}
                sx={{
                  bgcolor: "#075E54",
                  color: "white",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.1,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#064b43", boxShadow: "none" },
                  "&:disabled": {
                    bgcolor: "#e2e8f0",
                    color: "#64748b",
                  },
                }}
                startIcon={<Save size={16} />}
              >
                {saving ? "Saving..." : "Complete"}
              </Button>
            </div>
          </div>
        </section>
      )}
    </aside>
  );
};

export default PatientInfoPanel;
